import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Globe } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

const languageNames: Record<SupportedLanguage, string> = {
  it: 'IT',
  en: 'EN',
}

const languageFullNames: Record<SupportedLanguage, string> = {
  it: 'Italiano',
  en: 'English',
}

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
  isScrolled?: boolean
}

export function LanguageSelector({
  variant = 'desktop',
  isScrolled = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) return

    // Get current path without language prefix
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const currentPath = pathSegments.slice(1).join('/') // Remove language prefix

    // Find matching route key and get equivalent in new language
    let newPath = ''
    for (const [, routes] of Object.entries(localizedRoutes)) {
      if (routes[currentLang] === currentPath) {
        newPath = routes[newLang]
        break
      }
    }

    // If no match found, keep the same path
    if (!newPath && currentPath) {
      newPath = currentPath
    }

    i18n.changeLanguage(newLang)
    navigate(`/${newLang}${newPath ? `/${newPath}` : ''}`)
  }

  if (variant === 'mobile') {
    return (
      <div className="flex items-center gap-2 px-4 py-3">
        <Globe className="h-5 w-5 text-gray-500" />
        <div className="flex gap-2">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded transition-colors cursor-pointer',
                lang === currentLang
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {languageFullNames[lang]}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <Globe
        className={cn('h-4 w-4', isScrolled ? 'text-gray-600' : 'text-white/80')}
      />
      {supportedLanguages.map((lang, index) => (
        <span key={lang} className="flex items-center">
          <button
            onClick={() => switchLanguage(lang)}
            className={cn(
              'px-2 py-1 text-sm font-medium transition-colors rounded cursor-pointer',
              lang === currentLang
                ? isScrolled
                  ? 'text-gray-900 font-semibold'
                  : 'text-white font-semibold'
                : isScrolled
                  ? 'text-gray-500 hover:text-gray-900'
                  : 'text-white/60 hover:text-white'
            )}
          >
            {languageNames[lang]}
          </button>
          {index < supportedLanguages.length - 1 && (
            <span
              className={cn('text-sm', isScrolled ? 'text-gray-400' : 'text-white/40')}
            >
              |
            </span>
          )}
        </span>
      ))}
    </div>
  )
}
